import AuthLayout from "../../layout/AuthLayouts";
import { useGoogleLogin } from "@react-oauth/google";
import { useContext, useEffect, useState } from "react";

import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CircularProgress from "../../components/Generic/CircularProgress";
import PasswordValidator from "../../components/Generic/PasswordValidator";
import { GlobalContext } from "../../context";
import { googleHandler, signupHandler } from "../../context/actions/auth";
import {
  Referral,
  fetchSignUpLocation,
  isPasswordValid,
  validatePassword,
} from "../../utility";

export default function SignUpPage() {
  const {
    countryCodeState: { data },
    authDispatch,
  } = useContext(GlobalContext);
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 = options screen, 2 = form
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [telephone, setTelephone] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [referral, setReferral] = useState("");
  const [other, setOther] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [passwordError, setPasswordError] = useState({});

  const login = useGoogleLogin({
    onSuccess: (res) => handleGoogleSubmit(res),
  });

  useEffect(() => {
    const error = validatePassword(password);
    setPasswordError(error);
  }, [password]);

  useEffect(() => {
    fetchSignUpLocation().then((res) => {
      setCountry(res?.country);
      setRegion(res?.region);
      setCity(res?.city);
    });
  }, []);

  const handleGoogleSubmit = async (data) => {
    setGoogleLoading(true);
    const res = await googleHandler(
      { access_token: data?.access_token, location: { region, country, city } },
      authDispatch
    );
    if (res) navigate("/");
    setGoogleLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (isPasswordValid(passwordError)) {
      const res = await signupHandler(
        {
          email, password,
          referral: other === "" ? referral : other,
          firstname: firstName, lastname: lastName, telephone,
          location: { country, region, city },
          country_code: selectedCountryCode,
        },
        authDispatch
      );
      if (res) navigate("/vonverify");
    } else {
      toast.error("Password does not meet our requirements");
    }
    setLoading(false);
  };

  return (
    <AuthLayout>
      <div className="w-full">

        {step === 1 ? (
          /* ── STEP 1: Welcome / choose method ── */
          <>
            <div className="mb-10">
              <h1 className="text-3xl font-black text-gray-900 tracking-tight mb-2">
                Start your journey<span className="text-primary">.</span>
              </h1>
              <p className="text-gray-500 text-base font-light">
                Create your free E1X Academy account in seconds.
              </p>
            </div>

            {/* Google */}
            <button
              type="button"
              onClick={() => login()}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-3 border border-gray-200 hover:border-gray-400 bg-white hover:bg-gray-50 transition-all duration-300 py-4 text-gray-700 font-semibold text-sm mb-4"
            >
              {googleLoading ? <CircularProgress /> : (
                <>
                  <svg viewBox="0 0 48 48" className="w-5 h-5">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Continue with Google
                </>
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium uppercase tracking-widest">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <button
              type="button"
              onClick={() => setStep(2)}
              className="w-full bg-primary hover:bg-[#b0451e] text-white font-bold text-base py-5 transition-colors duration-300 flex items-center justify-center gap-3 group"
            >
              Sign up with Email
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4 group-hover:translate-x-1 transition-transform">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>

            <p className="mt-8 text-sm text-center text-gray-500 font-medium">
              Already have an account?{" "}
              <Link to="/sign-in" className="text-primary font-bold hover:underline">
                Sign In
              </Link>
            </p>
          </>
        ) : (
          /* ── STEP 2: Full registration form ── */
          <>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors mb-8"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </button>

            <div className="mb-8">
              <h2 className="text-2xl font-black text-gray-900 tracking-tight mb-1">
                Create Account<span className="text-primary">.</span>
              </h2>
              <p className="text-gray-500 text-sm font-light">Fill in your details to get started.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">First Name *</label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required placeholder="John"
                    className="w-full border-b border-gray-300 focus:border-primary pb-2 outline-none text-gray-900 text-sm font-medium bg-transparent transition-colors placeholder-gray-300" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Last Name *</label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required placeholder="Doe"
                    className="w-full border-b border-gray-300 focus:border-primary pb-2 outline-none text-gray-900 text-sm font-medium bg-transparent transition-colors placeholder-gray-300" />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Email Address *</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com"
                  className="w-full border-b border-gray-300 focus:border-primary pb-2 outline-none text-gray-900 text-sm font-medium bg-transparent transition-colors placeholder-gray-300" />
              </div>

              {/* Phone */}
              <div className="grid grid-cols-5 gap-3">
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Code *</label>
                  <select value={selectedCountryCode} onChange={e => setSelectedCountryCode(e.target.value)} required
                    className="w-full border-b border-gray-300 focus:border-primary pb-2 outline-none text-gray-900 text-sm font-medium bg-transparent transition-colors">
                    <option value="">+---</option>
                    {data?.length > 0 && data.map((cur, i) => (
                      <option key={i} value={cur.code}>{cur.code} {cur.name?.slice(0, 15)}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-3">
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Phone *</label>
                  <input type="tel" value={telephone} onChange={e => setTelephone(e.target.value)} required placeholder="08012345678"
                    className="w-full border-b border-gray-300 focus:border-primary pb-2 outline-none text-gray-900 text-sm font-medium bg-transparent transition-colors placeholder-gray-300" />
                </div>
              </div>

              {/* How did you hear */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">How did you hear about us? *</label>
                <select value={referral} onChange={e => setReferral(e.target.value)} required
                  className="w-full border-b border-gray-300 focus:border-primary pb-2 outline-none text-gray-900 text-sm font-medium bg-transparent transition-colors">
                  <option value="">Select...</option>
                  {Referral?.map((cur, i) => <option value={cur} key={i}>{cur}</option>)}
                </select>
              </div>
              {referral === "Other" && (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Please specify</label>
                  <input type="text" value={other} onChange={e => setOther(e.target.value)} required placeholder="Tell us how..."
                    className="w-full border-b border-gray-300 focus:border-primary pb-2 outline-none text-gray-900 text-sm font-medium bg-transparent transition-colors placeholder-gray-300" />
                </div>
              )}

              {/* Password */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Password *</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••"
                    className="w-full border-b border-gray-300 focus:border-primary pb-2 outline-none text-gray-900 text-sm font-medium bg-transparent transition-colors placeholder-gray-300 pr-8" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 bottom-2 text-gray-400 hover:text-gray-700">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                      {showPassword
                        ? <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        : <><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></>
                      }
                    </svg>
                  </button>
                </div>
                {password !== "" && (
                  <div className="mt-3">
                    <PasswordValidator password={password} passwordError={passwordError} />
                  </div>
                )}
              </div>

              <button type="submit" disabled={loading}
                className="w-full bg-primary hover:bg-[#b0451e] text-white font-bold text-base py-5 transition-colors duration-300 flex items-center justify-center gap-3 group mt-2">
                {loading ? <CircularProgress /> : (
                  <>
                    Create Account
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4 group-hover:translate-x-1 transition-transform">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </button>

              <p className="text-sm text-center text-gray-500 font-medium">
                Already have an account?{" "}
                <Link to="/sign-in" className="text-primary font-bold hover:underline">Sign In</Link>
              </p>
            </form>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
